<template>
  <div class="container-like"
    :key="render"
    :class="{ 'preview-mode': preview }"
    :style="{'padding-top': preview ? '0' : '90px'}">
    <!-- Preview Mode -->
    <div v-if="preview" class="preview-container">
      <div class="preview-samples">
        <div class="preview-subject-options">
          <!-- Subject thumbnails -->
          <div class="preview-thumbs" v-if="question.showImageOnTitle">
            <div v-for="(subject, idx) in previewSubjects"
                 :key="idx"
                 class="preview-thumb-box">
              <div v-if="subject.imageOnTitle?.[0]?.label"
                   class="preview-thumb-image"
                   :style="{
                     backgroundImage: `url(${subject.imageOnTitle[0].label})`,
                     backgroundSize: 'cover',
                     backgroundPosition: 'center'
                   }">
              </div>
              <div v-else class="preview-thumb-placeholder">
                <i class="material-icons">image</i>
              </div>
            </div>
            <div v-if="question.subjects.length > 2" class="preview-more">
              +{{ question.subjects.length - 2 }}
            </div>
          </div>

          <!-- Like/Dislike indicator -->
          <div class="preview-ui-type">
            <div class="like-dislike-indicator">
              <i class="material-icons dislike">thumb_down</i>
              <span class="separator"></span>
              <i class="material-icons like">thumb_up</i>
            </div>
          </div>

          <!-- Count text -->
          <div class="preview-count">
            {{ getPreviewCountText }}
          </div>
        </div>
      </div>
    </div>

    <!-- Survey Creation Mode -->
    <div v-else class="content-like">
      <div class="section-matrix-like" v-for="(item, idx) in question.subjects" :key="idx">
        <div class="section-label" v-if="!preview" :class="{'has-image': question.showImageOnTitle && !question.hideAllSubjectLabels}">
          <ImageWrapper v-if="question.showImageOnTitle"
              :uploaderId="`matrix-uploader-${idx}`"
              :uploaderTitle="$t('sendhub.set_image_text')"
              :imageProps="imagePropsDefault(question, idx)"
              :canDelete="false"
              :disableMenus="false"
              :menuOptions="[]"
              @resetAutoStartUpload="handleResetAutoStartUpload(idx)"
              @submenu-action="handleImageAction($event, idx)"
              class="matrix-like-image-wrapper"
            />
          <app-form-textarea-limited v-if="(question.showImageOnTitle && !question.hideAllSubjectLabels) || !question.showImageOnTitle"
            :inputMaxHeight="500"
            :inputMinHeight="20"
            v-model="item.label"
            :identify="`matrixlike-${idx}`"
            class="description-area"
            :colorText="colors.splashColor"
            name="matrixlike-description"
            @updateSurveyTitle="updateQuestionDescription($event,idx)"
          ></app-form-textarea-limited>
        </div>
        <div class="section-label" v-else>
          <app-heading level="h6" :style="{'color': colors.splashColor }">{{ item.label }}</app-heading>
        </div>
        <div class="section-rating">
          <div class="content-rating">
              <app-form-rate-active
                v-model="inputValue"
                :items="items"
                item-value="id"
              />
              <i class="material-icons up">thumb_up</i>
              <i class="material-icons down">thumb_down</i>
          </div>
          <div class="section-delete" v-if="!preview">
            <button @click="deleteItem(idx)">
              <i class="material-icons close">close</i>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="add-item" v-if="!preview">
      <button @click="addItem()"><i class="material-icons add">add</i></button>
    </div>
    <theme-style>
      .add-item > button {
        background-color: {{user.account.theme.colors.accent  || '#ff607d'}} !important;
      }
      .add-item > button > .add {
        color:{{ (user.account.theme.colors.primaryText === 'dark') ? '#302b3c' : '#fdfbff' }};
      }
    </theme-style>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ImageWrapper from '@/components/survey/ImageWrapper.vue'

export default {
  name: 'matrixlike',
  props: ['colors', 'preview', 'question'],
  components: {
    'app-form-rate-active': () => import('@binds-tech/binds-design-system/src/components/Form/RateActive'),
    'app-heading': () => import('@binds-tech/binds-design-system/src/components/Typography/Heading'),
    'app-form-textarea-limited': () => import('@binds-tech/binds-design-system/src/components/Form/TextareaLimited'),
    ImageWrapper
  },
  data () {
    return {
      render: 0,
      inputValue: null,
      items: [
        { id: 1, value: 1 },
        { id: 2, value: 2 }
      ]
    }
  },
  created () {
    if (!this.question.subjects) {
      const subjects = [
        { label: this.$i18n.t('survey.option_1') },
        { label: this.$i18n.t('survey.option_2') },
        { label: this.$i18n.t('survey.option_3') }
      ]

      const options = [
        { label: 'dislike', rating: 0 },
        { label: 'like', rating: 100 }
      ]

      this.$set(this.question, 'subjects', subjects)
      this.$set(this.question, 'options', options)

      // Initialize images for each subject
      subjects.forEach((_, index) => {
        this.getOrCreateImageForSubject(index)
      })

      this.$store.commit('survey/upsertQuestion', { ...this.question })
    }
  },
  methods: {
    updateQuestionDescription (e, idx) {
      this.question.subjects[idx].label = e
      this.$store.commit('survey/upsertQuestion', this.question)
    },
    addItem () {
      this.render += 1
      const newIndex = this.question.subjects.length
      this.question.subjects.push({
        label: this.$i18n.t('survey.option_added') + (newIndex + 1)
      })

      // Create image for the new subject
      this.getOrCreateImageForSubject(newIndex)

      this.$store.commit('survey/upsertQuestion', this.question)
      setTimeout(() => { this.render += 1 }, 200)
    },
    deleteItem (idx) {
      this.render += 1
      this.question.subjects.splice(idx, 1)

      // Reorder images to match new subject indices
      const newImageOnTitle = this.question.imageOnTitle.filter((_, index) => index !== idx)
      this.$set(this.question, 'imageOnTitle', newImageOnTitle)

      setTimeout(() => { this.render = true }, 200)
      this.$store.commit('survey/upsertQuestion', this.question)
    },
    imagePropsDefault (question, subjectIndex) {
      const imageOnTitle = this.getOrCreateImageForSubject(subjectIndex)

      return {
        id: question.id,
        success: Boolean(imageOnTitle.label),
        where: 'imageOnTitle',
        autoStartUpload: imageOnTitle?.autoStartUpload,
        label: imageOnTitle?.label,
        imageIndex: subjectIndex
      }
    },
    getOrCreateImageForSubject (subjectIndex) {
      if (!this.question.imageOnTitle) {
        this.$set(this.question, 'imageOnTitle', [])
      }

      return this.question.imageOnTitle[subjectIndex] || {
        label: 'https://img.binds.co/437e8dd8-82df-4a39-a30c-43ee8f8a6eea/-/crop/255x237/18,18/-/preview/',
        isHidden: false,
        value: `imagem ${subjectIndex + 1}`,
        imageSize: '',
        autoStartUpload: false
      }
    },
    handleResetAutoStartUpload (subjectIndex) {
      if (this.question.imageOnTitle?.[subjectIndex]) {
        this.question.imageOnTitle[subjectIndex].autoStartUpload = false
        this.$store.commit('survey/upsertQuestion', { ...this.question })
      }
    },
    handleImageAction (action, subjectIndex) {
      if (action === 'toggle-visibility') {
        const imageOnTitle = this.question.imageOnTitle[subjectIndex]
        if (imageOnTitle) {
          this.$set(imageOnTitle, 'isHidden', !imageOnTitle.isHidden)
          this.$store.commit('survey/upsertQuestion', { ...this.question })
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      user: 'account/getUser'
    }),
    previewSubjects () {
      // Return only first 2 subjects for preview
      return this.question.subjects.slice(0, 2)
    },
    getPreviewCountText () {
      const total = this.question.subjects.length
      return `${total} ${this.$tc('survey.subject_options', total)}`
    }
  }
}
</script>

<style lang="scss">
.container-like {
  width: 100%;
  min-height: 415px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;

  .content-like {
    width: 100%;
    min-height: 70%;
    margin-bottom: 15px;

    .section-matrix-like {
      min-height: 50px;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;

      .section-label {
        padding: 10px;
        min-height: 100%;
        gap: 10px;
        display: flex;
        align-items: center;

        &.has-image {
          flex: 1;
        }

        .matrix-like-image-wrapper {
          width: 100px;
          height: 100px;
          flex-shrink: 0;
        }

        .description-area {
          flex: 1;
          margin: 0 !important;
          width: 100%;

          .input {
            border: 1px dashed #cacaca !important;
            padding: 10px;
            width: 100%;
          }
        }
      }
      .section-rating {
        width: 35%;
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: space-around;
        .content-rating {
          position: relative;
          height: 80%;
          width: 70%;
          border-radius: 35px;
          box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.26);
          border: 1px solid #cacaca;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          .rating {
            z-index: 10;
            width: 100%;
            height: 100%;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            .rating__item:nth-child(2) {
              width: 50%;
              height: 100%;
              .rating__button {
                width: 100%;
                border-radius: 0;
                height: 100%;
                background: transparent !important;
              }
              .rating__button:hover {
                background: #ea492240 !important;
              }
              .active-rate {
                background: #ea492240 !important;
                transform: scale(1) !important;
                border: none !important;
              }
            }
            .rating__item:nth-child(5) {
              width: 50%;
              height: 100%;
              .rating__button {
                border-left: 1px solid #b6b6b6;
                width: 100%;
                border-radius: 0;
                height: 100%;
                background: transparent !important;
              }
              .rating__button:hover {
                background: #51ad5940 !important;
              }
              .active-rate {
                background: #51ad5940 !important;
                transform: scale(1) !important;
                border: none !important;
              }
            }
          }
          .up {
            position: absolute;
            right: 20px;
            color: #51ad59;
          }
          .down {
            position: absolute;
            left: 20px;
            color: #ea4922;
          }
        }
        .section-delete {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 20%;
          .close {
            color: gray;
          }
        }
      }
    }
  }
  .add-item {
    width: 85%;
    padding-top: 10px;
    display: flex;
    justify-content: flex-start;
    button {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      .add {
        color: #FFFFFF;
      }
    }
  }

  // Add new preview mode styles
  &.preview-mode {
    min-height: unset !important;
    padding: 0;
    margin: 0;
    height: auto;
    transform: none;

    .preview-container {
      padding: 6px;
      width: 100%;

      .preview-samples {
        background: #f8f8f8;
        border-radius: 8px;
        padding: 8px;
      }

      .preview-subject-options {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }

      .preview-thumbs {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-bottom: 2px;

        .preview-thumb-box {
          width: 24px;
          height: 24px;
          background-color: #f0f0f0;
          border: 1px solid #ddd;
          border-radius: 4px;
          overflow: hidden;

          .preview-thumb-image {
            width: 100%;
            height: 100%;
          }

          .preview-thumb-placeholder {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;

            i {
              font-size: 16px;
              color: #999;
            }
          }
        }

        .preview-more {
          background-color: var(--accent-color, #ff607d);
          color: white;
          font-size: 10px;
          padding: 2px 4px;
          border-radius: 4px;
          min-width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      .preview-ui-type {
        margin: 4px 0;

        .like-dislike-indicator {
          display: flex;
          align-items: center;
          background: white;
          border-radius: 12px;
          padding: 2px 8px;
          gap: 4px;
          border: 1px solid #eee;

          i {
            font-size: 14px;

            &.like {
              color: #51ad59;
            }

            &.dislike {
              color: #ea4922;
            }
          }

          .separator {
            height: 12px;
            width: 1px;
            background: #ddd;
            margin: 0 2px;
          }
        }
      }

      .preview-count {
        font-size: 10px;
        color: #666;
        text-align: center;
      }
    }
  }
}

</style>
